import React, { useState, useEffect, useRef } from "react";
import { QRCodeCanvas } from 'qrcode.react';

function IndexStatus() {
  const [buckets, setBuckets] = useState([]);
  const [newBucketName, setNewBucketName] = useState("");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const pollRef = useRef(null);

  const [photographers, setPhotographers] = useState([]);
  const [newPhotographerUser, setNewPhotographerUser] = useState("");
  const [newPhotographerPass, setNewPhotographerPass] = useState("");
  const [showQRFor, setShowQRFor] = useState(null);

  const fetchBuckets = async () => {
    try {
      const res = await fetch("/api/buckets");
      if (res.ok) {
        const data = await res.json();
        setBuckets(data);
      }
    } catch {
      setMessage("Could not reach backend.");
    }
  };

  const fetchPhotographers = async () => {
    try {
      const res = await fetch("/api/photographers");
      if (res.ok) {
        const data = await res.json();
        setPhotographers(data);
      }
    } catch {
      // ignore
    }
  };

  const fetchStatus = async () => {
    try {
      const res = await fetch("/api/preprocess/status");
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        return data;
      }
    } catch {
      return null;
    }
  };

  useEffect(() => {
    fetchBuckets();
    fetchPhotographers();
    fetchStatus();
    // Setup regular polling for status just in case a job is running in background
    const interval = setInterval(() => {
      fetchStatus();
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleAddBucket = async (e) => {
    e.preventDefault();
    if (!newBucketName) return;
    setLoading(true);
    setMessage("");
    try {
      const res = await fetch("/api/buckets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newBucketName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to add bucket");
      
      setNewBucketName("");
      fetchBuckets();
      setMessage(`Bucket '${newBucketName}' added successfully.`);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
    setLoading(false);
  };

  const handleDeleteBucket = async (bucketName) => {
    if (!window.confirm(`Are you sure you want to delete ${bucketName} from the index?`)) return;
    setLoading(true);
    setMessage("");
    try {
      const res = await fetch(`/api/buckets/${bucketName}`, { method: "DELETE" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to delete bucket");
      
      fetchBuckets();
      setMessage(data.message);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
    setLoading(false);
  };

  const handleAddPhotographer = async (e) => {
    e.preventDefault();
    if (!newPhotographerUser || !newPhotographerPass) return;
    setLoading(true);
    setMessage("");
    try {
      const res = await fetch("/api/photographers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: newPhotographerUser, password: newPhotographerPass }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to add photographer");
      
      setNewPhotographerUser("");
      setNewPhotographerPass("");
      fetchPhotographers();
      setMessage(`Photographer '${newPhotographerUser}' added successfully.`);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
    setLoading(false);
  };

  const handleDeletePhotographer = async (username) => {
    if (!window.confirm(`Revoke access for ${username}?`)) return;
    setLoading(true);
    setMessage("");
    try {
      const res = await fetch(`/api/photographers/${username}`, { method: "DELETE" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to delete photographer");
      
      fetchPhotographers();
      setMessage(data.message);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
    }
    setLoading(false);
  };

  const startPreprocessing = async (bucketName) => {
    setLoading(true);
    setMessage("");
    try {
      const res = await fetch(`/api/preprocess/${bucketName}`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to start");
      
      setMessage(`Indexing started for ${bucketName}...`);
      
      // Start intense polling
      pollRef.current = setInterval(async () => {
        const s = await fetchStatus();
        if (s && !s.running) {
          clearInterval(pollRef.current);
          setLoading(false);
          fetchBuckets();
          setMessage(s.message);
        }
      }, 1000);
    } catch (err) {
      setMessage(`Error: ${err.message}`);
      setLoading(false);
    }
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  return (
    <div className="max-w-[1000px] mx-auto w-full">
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-3xl font-extrabold">S3 Bucket Management</h2>
        <button 
          onClick={() => { fetchBuckets(); fetchStatus(); }}
          className="bg-bg3 text-white border border-borderBase px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/5 hover:border-borderHover transition-all flex items-center gap-2"
          disabled={loading}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 4v6h-6"></path><path d="M1 20v-6h6"></path><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
          Refresh
        </button>
      </div>
      <p className="text-textMuted mb-8 leading-relaxed text-lg">
        Add public AWS S3 buckets to index images. The system will incrementally index only the new images.
      </p>

      {message && (
        <div className="bg-[rgba(0,230,118,0.1)] border border-[rgba(0,230,118,0.3)] rounded-xl p-4 mb-6 text-base text-[#00e676]">
          {message}
        </div>
      )}

      {/* Add Bucket Form */}
      <form onSubmit={handleAddBucket} className="flex gap-4 mb-10 bg-bg2 p-6 rounded-2xl border border-borderBase flex-wrap md:flex-nowrap">
        <input
          type="text"
          placeholder="Enter S3 Bucket Name (e.g. ai-photobooth)"
          className="flex-1 bg-bg3 border border-borderBase rounded-xl px-5 py-3 text-white focus:outline-none focus:border-accentSolid transition-colors"
          value={newBucketName}
          onChange={(e) => setNewBucketName(e.target.value)}
          disabled={loading}
        />
        <button
          type="submit"
          className="bg-accent-gradient text-white px-8 py-3 rounded-xl font-semibold shadow-glow hover:-translate-y-1 hover:shadow-glow-strong transition-all active:translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap"
          disabled={loading || !newBucketName}
        >
          + Add Bucket
        </button>
      </form>

      {/* Photographer Management Section */}
      <div className="mb-12">
        <h3 className="text-2xl font-bold mb-4">Photographer Credentials</h3>
        <form onSubmit={handleAddPhotographer} className="flex gap-4 mb-6 bg-bg2 p-6 rounded-2xl border border-borderBase flex-wrap md:flex-nowrap">
          <input
            type="text"
            placeholder="Username"
            className="flex-1 bg-bg3 border border-borderBase rounded-xl px-5 py-3 text-white focus:outline-none focus:border-accentSolid transition-colors"
            value={newPhotographerUser}
            onChange={(e) => setNewPhotographerUser(e.target.value)}
            disabled={loading}
          />
          <input
            type="text"
            placeholder="Password"
            className="flex-1 bg-bg3 border border-borderBase rounded-xl px-5 py-3 text-white focus:outline-none focus:border-accentSolid transition-colors"
            value={newPhotographerPass}
            onChange={(e) => setNewPhotographerPass(e.target.value)}
            disabled={loading}
          />
          <button
            type="submit"
            className="bg-bg3 text-white border border-borderBase px-8 py-3 rounded-xl font-semibold hover:bg-white/5 hover:border-borderHover transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            disabled={loading || !newPhotographerUser || !newPhotographerPass}
          >
            Create Login
          </button>
        </form>

        <div className="flex flex-wrap gap-3">
          {photographers.map(p => (
            <div key={p.username} className="flex flex-col gap-1 bg-bg3 border border-borderBase py-2 px-4 rounded-xl text-sm min-w-[200px]">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-white">{p.username}</span>
                <button 
                  onClick={() => handleDeletePhotographer(p.username)}
                  className="text-red-400 hover:text-red-300 font-bold ml-2"
                  title="Revoke Access"
                >
                  &times;
                </button>
              </div>
              <div className="text-textMuted text-xs font-mono bg-bg2 px-2 py-1 rounded border border-borderHover break-all">
                {p.password}
              </div>
            </div>
          ))}
          {photographers.length === 0 && <span className="text-textMuted text-sm">No photographers found.</span>}
        </div>
      </div>

      {/* Buckets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {buckets.map((b) => (
          <div key={b.name} className="bg-bg2 border border-borderBase rounded-2xl p-6 backdrop-blur-md transition-all hover:border-borderHover">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-white truncate pr-4" title={b.name}>{b.name}</h3>
              <div className="flex gap-2">
                <button 
                  onClick={() => setShowQRFor(showQRFor === b.name ? null : b.name)}
                  className="text-textMuted hover:text-white transition-colors p-1"
                  title="Show QR Code"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
                </button>
                <button 
                  onClick={() => handleDeleteBucket(b.name)}
                  disabled={loading || (status?.running && status?.bucket === b.name)}
                  className="text-red-400 hover:text-red-300 transition-colors p-1"
                  title="Remove Bucket"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                </button>
              </div>
            </div>
            
            <div className="flex justify-between mb-6 text-[0.95rem]">
              <div className="flex flex-col">
                <span className="text-textMuted mb-1">Total in S3</span>
                <span className="font-semibold">{b.total_in_s3}</span>
              </div>
              <div className="flex flex-col text-right">
                <span className="text-textMuted mb-1">Indexed</span>
                <span className="font-semibold text-accentSolid">{b.indexed_count}</span>
              </div>
            </div>

            {/* Progress Bar for this bucket */}
            {status?.running && status?.bucket === b.name ? (
              <div className="mb-4">
                <div className="bg-bg3 rounded-full h-2.5 mb-2 overflow-hidden">
                  <div 
                    className="h-full bg-accent-gradient rounded-full transition-[width] duration-300 shadow-glow" 
                    style={{ width: `${Math.round((status.processed / status.total) * 100) || 0}%` }} 
                  />
                </div>
                <div className="text-xs text-textMuted flex justify-between">
                  <span>{status.processed}/{status.total} new images</span>
                  <span>{status.faces_found} faces</span>
                </div>
              </div>
            ) : (
              <button
                className="w-full bg-bg3 text-white border border-borderBase py-3 rounded-xl font-medium transition-all hover:bg-white/5 hover:border-accentSolid disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => startPreprocessing(b.name)}
                disabled={loading || status?.running}
              >
                ⚡ Index Now
              </button>
            )}

            {/* QR Code Section */}
            {showQRFor === b.name && (
              <div className="mt-6 pt-6 border-t border-borderBase flex flex-col items-center animate-fadeIn">
                <p className="text-sm text-textMuted mb-4 text-center">Scan to open event gallery</p>
                <div className="bg-white p-4 rounded-xl shadow-glow relative group">
                  <QRCodeCanvas 
                    id={`qr-${b.name}`}
                    value={`${window.location.origin}/?from=${b.name}`} 
                    size={180} 
                    bgColor={"#ffffff"}
                    fgColor={"#000000"}
                  />
                </div>
                <button
                  onClick={() => {
                    const canvas = document.getElementById(`qr-${b.name}`);
                    if (canvas) {
                      const url = canvas.toDataURL("image/png");
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `${b.name}-qrcode.png`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }
                  }}
                  className="mt-4 bg-bg3 text-white border border-borderBase px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/5 transition-all flex items-center gap-2"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                  Download QR Code
                </button>
                <a 
                  href={`/?from=${b.name}`} 
                  target="_blank" 
                  rel="noreferrer"
                  className="mt-3 text-sm font-medium text-accentSolid hover:text-white transition-colors"
                >
                  {`${window.location.host}/?from=${b.name}`}
                </a>
              </div>
            )}
          </div>
        ))}

        {buckets.length === 0 && (
          <div className="col-span-full text-center py-12 bg-bg2 rounded-2xl border border-borderBase text-textMuted">
            No buckets tracked yet. Add one above to get started.
          </div>
        )}
      </div>
    </div>
  );
}

export default IndexStatus;
